import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useForm } from '../contexts/FormContext';

const BookingForm: React.FC = () => {
  const { translations, language } = useLanguage();
  const { formContainerRef, focusAndCenterForm } = useForm();
  const location = useLocation();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    treatment: '',
    insurance: '',
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    message: ''
  });

  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const treatment = params.get('treatment');
    if (treatment) {
      setFormData(prev => ({ ...prev, treatment }));
    }
    // As per user request: Always start at Step 1, even if treatment is pre-selected
    setStep(1);

    // GTM: Wizard gestartet
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'wizard_start',
        wizard_name: 'booking_form',
        language: language
      });
    }
  }, [location.search, language]);

  useEffect(() => {
    focusAndCenterForm();

    // GTM: Step View
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'wizard_step_view',
        wizard_name: 'booking_form',
        wizard_step: step,
        language: language
      });
    }
  }, [focusAndCenterForm, step, language]);

  const treatments = [
    { id: 'cleaning', label: translations.bookingWizard.treatments.cleaning },
    { id: 'returningCleaning', label: translations.bookingWizard.treatments.returningCleaning },
    { id: 'general', label: translations.bookingWizard.treatments.consultation },
    { id: 'cosmetic', label: translations.bookingWizard.treatments.cosmetic },
    { id: 'secondOpinion', label: translations.bookingWizard.treatments.secondOpinion },
    { id: 'emergency', label: translations.bookingWizard.treatments.emergency },
    { id: 'whitening', label: translations.bookingWizard.treatments.whitening },
    { id: 'botox', label: translations.bookingWizard.treatments.botox },
    { id: 'tmj', label: translations.bookingWizard.treatments.tmj },
    { id: 'pediatric', label: translations.bookingWizard.treatments.pediatric },
  ];

  const handleTreatmentClick = (id: string) => {
    setFormData({ ...formData, treatment: id });
    setStep(2);

    // GTM: Treatment ausgewählt
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'wizard_treatment_selected',
        treatment_id: id,
        wizard_name: 'booking_form'
      });
    }
  };

  const handleInsuranceSelect = (insurance: string) => {
    setFormData({ ...formData, insurance });
    setStep(3);

    // GTM: Versicherung ausgewählt
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'wizard_insurance_selected',
        insurance_type: insurance,
        wizard_name: 'booking_form'
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: false });
    }
  };

  const validateStep3 = () => {
    const errors: Record<string, boolean> = {
      name: formData.name.trim() === '',
      email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email),
      phone: formData.phone.trim() === '',
      date: formData.date === '',
      time: formData.time === ''
    };
    setFormErrors(errors);
    return !Object.values(errors).some(error => error);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep3()) return;

    setIsSubmitting(true);
    setSubmitError(false);

    // Get the actual label for the selected treatment ID
    const selectedTreatment = treatments.find(t => t.id === formData.treatment || t.id.includes(formData.treatment));
    const treatmentLabel = selectedTreatment ? selectedTreatment.label : formData.treatment;

    try {
      const response = await fetch('/api/email/booking/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          treatment: treatmentLabel,
          treatmentId: formData.treatment,
          language
        }),
      });

      const data = await response.json();
      if (data.success) {
        setSubmitSuccess(true);

        // GTM: Wizard erfolgreich abgeschlossen
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'wizard_complete',
            wizard_name: 'booking_form',
            treatment_id: formData.treatment,
            insurance_type: formData.insurance,
            treatment_label: treatmentLabel
          });
        }
      } else {
        setSubmitError(true);
      }
    } catch (error) {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    navigate('/');
  };

  return (
    <div
      id="booking-form"
      ref={formContainerRef}
      className="bg-white rounded-xl shadow-2xl overflow-hidden max-w-2xl mx-auto border border-gray-200 mt-12 mb-24 min-h-[600px] flex flex-col font-sans"
    >
      {/* NexHealth Style Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <button
          onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}
          className="flex items-center text-gray-600 hover:text-navy transition-colors font-medium text-sm"
        >
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {translations.bookingWizard.navigation.back}
        </button>
        <span className="font-bold text-navy tracking-tight">SeaDent Mallorca</span>
        <button
          onClick={handleClose}
          className="text-gray-400 hover:text-navy transition-colors"
          title={translations.bookingWizard.navigation.close}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-grow p-8">
        {submitSuccess ? (
          <div className="h-full flex flex-col items-center justify-center text-center space-y-6 py-12 animate-fadeIn">
            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center text-green-500 shadow-sm">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="space-y-2">
              <h4 className="text-3xl font-bold text-navy">{translations.booking.form.success.split('!')[0]}!</h4>
              <p className="text-gray-500 text-lg">{translations.booking.form.success.split('!')[1] || ''}</p>
            </div>
            <button onClick={handleClose} className="btn btn-primary px-8">OK</button>
          </div>
        ) : (
          <div className="max-w-xl mx-auto">
            {/* Step titles match NexHealth style */}
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
              {step === 1 && translations.bookingWizard.steps.treatment}
              {step === 2 && translations.bookingWizard.steps.insurance}
              {step === 3 && translations.bookingWizard.steps.dateTime}
            </h2>

            {/* Step 1: Treatment Selection */}
            {step === 1 && (
              <div className="space-y-3 animate-fadeIn">
                {treatments.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => handleTreatmentClick(t.id)}
                    className={`w-full flex items-center justify-between p-4 border rounded-lg transition-all text-left group ${formData.treatment === t.id || formData.treatment === t.id.split('_')[0] ? 'border-light-blue bg-light-blue bg-opacity-5 ring-1 ring-light-blue' : 'border-gray-200 hover:border-gray-400'
                      }`}
                  >
                    <span className={`font-semibold text-gray-700 group-hover:text-navy ${formData.treatment === t.id || formData.treatment === t.id.split('_')[0] ? 'text-navy' : ''}`}>
                      {t.label}
                    </span>
                    <svg className="w-5 h-5 text-gray-300 group-hover:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ))}
              </div>
            )}

            {/* Step 2: Insurance Selection */}
            {step === 2 && (
              <div className="space-y-8 py-4 animate-fadeIn">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder={translations.bookingWizard.insurance.search}
                    className="block w-full pl-11 pr-4 py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-light-blue focus:border-light-blue shadow-sm outline-none transition-all"
                    autoFocus
                    onChange={(e) => setFormData({ ...formData, insurance: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && formData.insurance.trim()) {
                        setStep(3);
                      }
                    }}
                  />
                </div>
                <div className="flex flex-col items-center space-y-4">
                  <button
                    onClick={() => {
                      setStep(3);
                      // GTM: Versicherung manuell eingegeben/bestätigt
                      if (window.dataLayer) {
                        window.dataLayer.push({
                          event: 'wizard_insurance_selected',
                          insurance_type: formData.insurance,
                          wizard_name: 'booking_form'
                        });
                      }
                    }}
                    disabled={!formData.insurance.trim()}
                    className={`btn btn-primary w-full py-4 ${!formData.insurance.trim() ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {translations.bookingWizard.navigation.next}
                  </button>
                  <button
                    onClick={() => handleInsuranceSelect('none')}
                    className="text-gray-400 font-medium hover:text-navy underline transition-colors text-sm"
                  >
                    {translations.bookingWizard.insurance.none}
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Date, Time & Final Info */}
            {step === 3 && (
              <div className="animate-fadeIn">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                  {/* Left Column: Practice Info */}
                  <div className="md:col-span-5 space-y-6 border-b md:border-b-0 md:border-r border-gray-100 pb-6 md:pb-0 md:pr-8">
                    <div className="space-y-1">
                      <h3 className="font-bold text-navy text-lg">SeaDent Mallorca</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">
                        Avenida de Jaime III № 3<br />
                        07012 Palma<br />
                        Illes Balears, España
                      </p>
                    </div>

                    <div className="rounded-lg overflow-hidden h-40 shadow-inner bg-gray-50 border border-gray-100">
                      <iframe
                        title="SeaDent Mallorca Preview"
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3075.651!2d2.6437627!3d39.571492!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1297925ae0978b45%3A0x39247b6f6334df60!2sSeaDent!5e0!3m2!1sen!2ses!4v1652786526784!5m2!1sen!2ses"
                        width="100%"
                        height="100%"
                        style={{ border: 0, filter: 'grayscale(0.1)' }}
                        loading="lazy"
                      ></iframe>
                    </div>
                  </div>

                  {/* Right Column: Date, Time & Info */}
                  <form onSubmit={handleSubmit} className="md:col-span-7 space-y-6">
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider">{translations.common.date}</label>
                          <input
                            type="date"
                            name="date"
                            value={formData.date}
                            min={new Date().toISOString().split('T')[0]}
                            onChange={handleChange}
                            className={`w-full p-3 border rounded focus:ring-2 focus:ring-light-blue outline-none text-sm ${formErrors.date ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider">{translations.common.time}</label>
                          <select
                            name="time"
                            value={formData.time}
                            onChange={handleChange}
                            className={`w-full p-3 border rounded focus:ring-2 focus:ring-light-blue outline-none bg-white text-sm ${formErrors.time ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                          >
                            <option value="">--:--</option>
                            <option value="09:00">09:00</option>
                            <option value="10:00">10:00</option>
                            <option value="11:00">11:00</option>
                            <option value="12:00">12:00</option>
                            <option value="14:00">14:00</option>
                            <option value="15:00">15:00</option>
                            <option value="16:00">16:00</option>
                          </select>
                        </div>
                      </div>

                      <div className="space-y-3 pt-2">
                        <input
                          type="text"
                          name="name"
                          placeholder={translations.booking.form.name}
                          value={formData.name}
                          onChange={handleChange}
                          className={`w-full p-3 border rounded focus:ring-2 focus:ring-light-blue outline-none text-sm transition-all ${formErrors.name ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                        />
                        <input
                          type="email"
                          name="email"
                          placeholder={translations.booking.form.email}
                          value={formData.email}
                          onChange={handleChange}
                          className={`w-full p-3 border rounded focus:ring-2 focus:ring-light-blue outline-none text-sm transition-all ${formErrors.email ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                        />
                        <input
                          type="tel"
                          name="phone"
                          placeholder={translations.booking.form.phone}
                          value={formData.phone}
                          onChange={handleChange}
                          className={`w-full p-3 border rounded focus:ring-2 focus:ring-light-blue outline-none text-sm transition-all ${formErrors.phone ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                        />
                        <textarea
                          name="message"
                          placeholder={translations.booking.form.message}
                          value={formData.message}
                          onChange={handleChange}
                          rows={2}
                          className="w-full p-3 border border-gray-200 rounded focus:ring-2 focus:ring-light-blue outline-none resize-none text-sm transition-all"
                        ></textarea>
                      </div>
                    </div>

                    {submitError && (
                      <div className="p-3 bg-red-50 text-red-700 rounded text-xs border border-red-100 animate-shake">
                        {translations.booking.form.error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-4 text-lg font-bold bg-navy text-white rounded hover:bg-opacity-95 transform transition-all active:scale-[0.98] shadow-lg disabled:opacity-50"
                    >
                      {isSubmitting ? translations.booking.form.submitting || translations.booking.form.submit : translations.booking.form.submit}
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Branding Footer */}
      <div className="border-t border-gray-100 px-6 py-8 flex flex-col items-center space-y-4">
        <div className="text-center text-xs text-gray-400 font-medium space-y-1">
          <p>SeaDent Mallorca</p>
          <p>Avenida de Jaime III № 3, Palma, Mallorca</p>

        </div>

        <div className="pt-4 flex flex-col items-center opacity-40">
          <span className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">{translations.bookingWizard.instantBookingBy}</span>
          <div className="flex items-center">
            <span className="text-lg font-black tracking-tighter text-gray-900">SEADENT</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingForm;
